import { z } from 'zod';
import { OrganizationSchema } from '../OrganizationSchema';
import { CareDocumentRefSchema } from './care-documents';
import {
  CreateEbpPatientSchema,
  EbpPatientSchema,
  UpdateEbpPatientSchema,
} from './EbpPatientSchema';
import { IObject } from '../IObject';
import { EbpCaseStatus } from '../enums';
import { EbpCaseReturnedToWorkSchema } from './EbpCaseReturnedToWorkSchema';
import { EbpCaseProgressSchema } from './EbpCaseProgressSchema';
import { EbpCaseSupportSchema } from './support/EbpCaseSupportSchema';
import { EbpCaseAppointmentsSchema } from './EbpCaseAppointmentsSchema';
import { dateValidation } from '../../validation';

// As it is sent from UI or API requests
export const CreateEbpCaseSchema = z.object({
  patient: CreateEbpPatientSchema,
  // either legacy perci code or new Medplum id
  organizationId: z.string().nullish(),
  // fhirId (GUID) of the expert from the external platform
  expertId: z.string().nullish(),
  // The EBP referral the member signed up through — the `referralId` of the
  // sign-up token. Sent by the Platform when the case is created at sign-up:
  // that referral, and no other, is attached to the case and marked
  // `account_created` (PPL-4196).
  referralId: z.string().nullish(),
  // Makes the request safe to repeat (PPL-4196): the Platform generates it
  // once per case it asks for and sends the same key on every retry and
  // Pub/Sub redelivery. A case is created for a key at most once; a repeat
  // answers with that case. Absent, every request creates a new case.
  idempotencyKey: z.string().trim().min(1).max(200).nullish(),
});

export const UpdateEbpCaseSchema = z.object({
  // Optional, with the empty object yup used to cast a missing `patient` into:
  // a PUT carrying one field (which is how the EBP UI sends an update) must
  // not be rejected for leaving the patient out. Every field of
  // UpdateEbpPatientSchema is itself optional, so `{}` parses to `{}`.
  patient: UpdateEbpPatientSchema.default({}),
  expertId: z.string().nullish(),
  consentShareDataToInsurerAndEmployer: z.boolean().optional(),
  improvedKeyChallenge: z.boolean().nullish(),
  followedUpAfterPcp: z.boolean().optional(),
  returnedToWork: EbpCaseReturnedToWorkSchema.nullish(),
  estimatedDischargeDate: dateValidation().nullish(),
});

// As it is stored in the database
export const EbpCaseSchema = UpdateEbpCaseSchema.pick({
  expertId: true,
  consentShareDataToInsurerAndEmployer: true,
  improvedKeyChallenge: true,
  followedUpAfterPcp: true,
  returnedToWork: true,
  estimatedDischargeDate: true,
}).extend({
  status: z.enum(EbpCaseStatus),
  patient: EbpPatientSchema,
  // calculated field
  organization: OrganizationSchema,
  hasAppointment: z.boolean().optional(),
  appointments: EbpCaseAppointmentsSchema,

  // calculated field
  followedUp: z.boolean().optional(),
  forms: z.object({
    mycawInitialFormId: z.string().optional(),
    mycawFollowUpFormId: z.string().optional(),
    mdtFeedbackFormId: z.string().optional(),
    // obsolete, divided on two: rtw-se and fatigue
    workFocusedFormId: z.string().optional(),
    rtwSeFormId: z.string().optional(),
    fatigueFormId: z.string().optional(),
  }),
  documents: z.object({
    pcp: CareDocumentRefSchema,
    workFocusedPlan: CareDocumentRefSchema,
    endOfCareSummary: CareDocumentRefSchema,
    interimCareSummary: CareDocumentRefSchema,
    initialAssessmentSummary: CareDocumentRefSchema,
    returnToWorkPlan: CareDocumentRefSchema,
  }),
  followedUpAfterPcp: z.boolean().optional(),
  referralId: z.string().optional(),
  // a referral was sent before the case creation, so we can differentiate the self-referred cases
  hasReferralBeforeCase: z.boolean().optional(),
  dischargedAt: dateValidation().nullable(),

  support: EbpCaseSupportSchema,

  progress: EbpCaseProgressSchema,
});

export type CreateEbpCase = z.infer<typeof CreateEbpCaseSchema>;
export type UpdateEbpCase = z.infer<typeof UpdateEbpCaseSchema>;
export type EbpCase = IObject.HasIdTimestamped<z.infer<typeof EbpCaseSchema>>;
